import {
  alpha,
  Box,
  Button,
  CircularProgress,
  EyebrowText,
  Icons,
  iconSize,
  InputAdornment,
  Link,
  palette,
  Stack,
  TextField,
  Typography,
  fontStacks,
} from '@munifocus/ui';
import { useState, type FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { AuthShell, BackToSignIn } from '../components/auth/AuthShell';
import { AuthErrorBanner, FieldLabel, fieldSx, validateEmail } from '../components/auth/authFields';

const monoSx = {
  fontFamily: fontStacks.mono,
  color: palette.text.primary,
} as const;

export function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { requestPasswordReset } = useAuth();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    const next = validateEmail(email);
    setEmailError(next);
    if (next) return;

    setError(null);
    setSubmitting(true);
    try {
      // The endpoint always succeeds regardless of whether the account exists —
      // we never reveal that here either.
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch {
      setError(t('gov.forgot.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = () => {
    setResent(true);
    void requestPasswordReset(email.trim()).catch(() => {
      /* best-effort resend — the confirmation copy is already shown */
    });
  };

  return (
    <AuthShell top={<BackToSignIn />}>
      {sent ? (
        <Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2.5,
              color: palette.brand.accent,
              backgroundColor: palette.brand.accentTint,
              border: `1px solid ${alpha(palette.brand.accent, 0.25)}`,
            }}
          >
            <Icons.MailCheck size={iconSize.xl} />
          </Box>

          <Typography variant="h2" sx={{ color: palette.text.primary, mb: 1.25 }}>
            {t('gov.forgot.sentTitle')}
          </Typography>
          <Typography
            sx={{ fontSize: 15, lineHeight: 1.55, color: palette.text.secondary, maxWidth: 380 }}
          >
            <Trans
              i18nKey="gov.forgot.sentBody"
              values={{ email: email.trim() }}
              components={{ mail: <Box component="span" sx={monoSx} /> }}
            />
          </Typography>

          <Button
            type="button"
            variant="contained"
            fullWidth
            onClick={() => navigate('/login')}
            sx={{ mt: 3.5, py: 1.4, borderRadius: '28px', fontSize: 15.5 }}
          >
            {t('gov.login.backToSignIn')}
          </Button>

          <Typography
            sx={{ mt: 2, textAlign: 'center', fontSize: 14, color: palette.text.secondary }}
          >
            {t('gov.forgot.resendPrompt')}{' '}
            <Link
              component="button"
              type="button"
              onClick={onResend}
              underline="hover"
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: palette.brand.accent,
                verticalAlign: 'baseline',
              }}
            >
              {t('gov.forgot.resend')}
            </Link>
          </Typography>

          {resent && (
            <Typography
              sx={{ mt: 1, textAlign: 'center', fontSize: 13, color: palette.semantic.success }}
            >
              {t('gov.forgot.resent')}
            </Typography>
          )}
        </Box>
      ) : (
        <>
          <Box>
            <EyebrowText sx={{ color: palette.brand.accent, mb: 1 }}>
              {t('gov.forgot.eyebrow')}
            </EyebrowText>
            <Typography variant="h2" sx={{ color: palette.text.primary, mb: 1.25 }}>
              {t('gov.forgot.title')}
            </Typography>
            <Typography
              sx={{ fontSize: 15, lineHeight: 1.5, color: palette.text.secondary, maxWidth: 380 }}
            >
              {t('gov.forgot.subtitle')}
            </Typography>
          </Box>

          <Box component="form" onSubmit={onSubmit} noValidate sx={{ mt: 3 }}>
            <Stack spacing={2.25}>
              {error && <AuthErrorBanner>{error}</AuthErrorBanner>}

              <Box>
                <FieldLabel htmlFor="forgot-email">{t('gov.login.workEmail')}</FieldLabel>
                <TextField
                  id="forgot-email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  autoFocus
                  fullWidth
                  placeholder={t('gov.login.emailPlaceholder')}
                  value={email}
                  error={Boolean(emailError)}
                  helperText={emailError ? t(`common.validation.${emailError}`) : undefined}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(undefined);
                    if (error) setError(null);
                  }}
                  onBlur={() => setEmailError(email.trim() ? validateEmail(email) : undefined)}
                  sx={fieldSx}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icons.Mail size={iconSize.lg} color={palette.text.muted} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting}
                sx={{
                  py: 1.4,
                  borderRadius: '28px',
                  fontSize: 15.5,
                  '&.Mui-disabled': {
                    backgroundColor: alpha(palette.brand.accent, 0.7),
                    color: palette.text.onDark,
                  },
                }}
              >
                {submitting ? (
                  <CircularProgress size={18} thickness={5} sx={{ color: 'inherit' }} />
                ) : (
                  t('gov.forgot.submit')
                )}
              </Button>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.25,
                  p: 1.75,
                  borderRadius: '10px',
                  backgroundColor: palette.background.parch,
                  border: `1px solid ${palette.border.line}`,
                }}
              >
                <Box
                  component="span"
                  sx={{ color: palette.brand.accent, display: 'flex', mt: '1px' }}
                >
                  <Icons.ShieldCheck size={iconSize.lg} />
                </Box>
                <Typography
                  sx={{ fontSize: 13.5, lineHeight: 1.45, color: palette.text.secondary }}
                >
                  {t('gov.forgot.expiryNote')}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </>
      )}
    </AuthShell>
  );
}
